<template lang="pug">
yz-card.mt-4(outlined)
  .pa-6
    .yz-heading-8.text-neutral-700 Pricing
    div.d-flex.align-center.mt-2
      .yz-regular-body-sm Set default pricing tier for this item. You can manage your pricing tier in settings.
      yz-btn(label="Go to pricing tier setting" underline append-icon="$yzi-arrow-up-right" @click="$emit('go-to-price-tiers')")
    .yz-heading-9.text-neutral-700.mt-6 Set default pricing
    .yz-regular-body-sm.mt-1 This pricing will be shared across all branches.
    template(v-if="pricingTiers.length === 0 && !isFetchingPricingTiers")
      div.empty-box.mt-6.pa-8(align="center")
        v-avatar(color="neutral300" size="54")
          v-icon(color="neutral500" size="30") mdi-tag-outline
        .yz-heading-8.text-neutral-700.text-center.mt-6 You don't have any pricing tier yet
        .yz-regular-body-sm.text-neutral-500.text-center.mt-1 Set your pricing tier now
        yz-btn.mt-6(label="Set your pricing tier" size="md" @click="$emit('go-to-price-tiers')")
    template(v-else)
      yz-simple-table.mt-3
        template(v-slot:default)
          thead
            tr
              th.yz-medium-body-xs.text-neutral-700 Price tier
              th.yz-medium-body-xs.text-neutral-700 Payment method
              th.yz-medium-body-xs.text-neutral-700 Default price
          tbody
            template(v-for="tier in localTiers")
              tr(:key="tier.id")
                td.py-3
                  .yz-heading-9.text-neutral-700 {{ tier.name }}
                  .yz-regular-body-sm {{ tier.description }}
                td.py-3
                  .d-flex.align-center.gap-2
                    div
                      .yz-regular-body-xs.text-neutral-700 Panel: {{ tier.panelText }}
                      .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ tier.selfPayText }}
                    yz-icon.text-neutral-500.cursor-pointer(v-if="tier.panelText !== 'None included'" size="16" @click="$emit('open-tier-details', tier)") mdi-information-outline
                td.py-3
                  yz-text-field(v-model="tier.price" placeholder="0" :prefix="currencySymbol" type="number" size="lg" :rules="costRules" @input="onTierPriceChange")
    //- Pricing overrides
    yz-divider.my-5
    .d-flex.align-start.justify-space-between
      div
        .yz-heading-9.text-neutral-700 Add default pricing override
        .yz-regular-body-sm.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
      yz-btn(v-if="priceOverrides.length === 0" label="Add price override" size="sm" @click="$emit('open-override-dialog')")
    template(v-if="priceOverrides.length > 0")
      .d-flex.align-center.justify-end.mt-4.mb-2
        yz-btn(label="Add price override" size="sm" @click="$emit('open-override-dialog')")
      yz-simple-table
        template(v-slot:default)
          thead
            tr
              th.yz-medium-body-xs.text-neutral-700 Branches
              th.yz-medium-body-xs.text-neutral-700 Price tier
              th.yz-medium-body-xs.text-neutral-700 Payment method
              th.yz-medium-body-xs.text-neutral-700 Override price
              th
          tbody
            template(v-for="(override, oIdx) in priceOverrides")
              template(v-for="branchId in override.branches")
                tr.cursor-pointer(:key="'ob-' + oIdx + '-' + branchId" :class="{ 'override-branch-expanded': isOverrideBranchExpanded(oIdx, branchId) }" @click="onToggleOverrideBranch(oIdx, branchId)")
                  td.py-3
                    .d-flex.align-center.gap-2
                      .yz-heading-9.text-neutral-700 {{ getBranchName(branchId) }}
                      yz-icon(size="16") {{ isOverrideBranchExpanded(oIdx, branchId) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  td
                  td
                  td
                  td.py-3
                    yz-btn(v-if="branchId === override.branches[0]" label="Edit" size="sm" outlined @click.stop="$emit('edit-override', oIdx)")
                template(v-if="isOverrideBranchExpanded(oIdx, branchId)")
                  template(v-for="(tierData, tIdx) in override.priceTiers")
                    tr(:key="'ot-' + oIdx + '-' + branchId + '-' + tIdx")
                      td
                      td.py-3
                        .yz-heading-9.text-neutral-700 {{ getOverrideTierName(tierData) }}
                        .yz-regular-body-sm {{ getOverrideTierDescription(tierData) }}
                      td.py-3
                        .d-flex.align-center.gap-2
                          div
                            .yz-regular-body-xs.text-neutral-700 Panel: {{ getOverrideTierPanelText(tierData) }}
                            .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ getOverrideTierSelfPayText(tierData) }}
                          yz-icon.text-neutral-500.cursor-pointer(v-if="getOverrideTierPanelText(tierData) !== 'None included'" size="16" @click.stop="$emit('open-tier-details', getTierById(tierData.price_tier))") mdi-information-outline
                      td.py-3
                        .d-flex.align-center.justify-end.gap-1
                          .yz-regular-body-sm.text-neutral-700(v-if="tierData.price != null && tierData.price !== ''") {{ tierData.price * currencyMultiplier | displayPrice }}
                          .yz-regular-body-sm.text-neutral-700(v-else) —
                      td
</template>

<script>
export default {
  props: {
    pricingTiers: { type: Array, default: () => [] },
    isFetchingPricingTiers: { type: Boolean, default: false },
    priceOverrides: { type: Array, default: () => [] },
    branches: { type: Array, default: () => [] },
    costRules: { type: Array, default: () => [] },
    currencySymbol: { type: String, default: '' },
    currencyMultiplier: { type: Number, default: 1 },
    currencyExponent: { type: Number, default: 2 },
  },
  data () {
    return {
      localTiers: [],
      expandedOverrideBranches: {},
    }
  },
  watch: {
    pricingTiers: {
      handler (val) {
        this.localTiers = val.map(t => ({ ...t }))
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    onTierPriceChange () {
      this.$emit('update:pricingTiers', this.localTiers.map(t => ({ ...t })))
    },
    getBranchName (branchId) {
      const branch = this.branches.find(b => b.id === branchId)
      return branch ? branch.name : branchId
    },
    getTierById (tierId) {
      return this.localTiers.find(t => t.id === tierId) || null
    },
    getOverrideTierName (tierData) {
      if (typeof tierData === 'object' && tierData.name) return tierData.name
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.name : ''
    },
    getOverrideTierDescription (tierData) {
      if (typeof tierData === 'object' && tierData.description != null) return tierData.description || ''
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.description : ''
    },
    getOverrideTierPanelText (tierData) {
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.panelText : 'None included'
    },
    getOverrideTierSelfPayText (tierData) {
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.selfPayText : 'None included'
    },
    onToggleOverrideBranch (oIdx, branchId) {
      const key = `${oIdx}-${branchId}`
      this.$set(this.expandedOverrideBranches, key, !this.expandedOverrideBranches[key])
    },
    isOverrideBranchExpanded (oIdx, branchId) {
      return !!this.expandedOverrideBranches[`${oIdx}-${branchId}`]
    },
  },
}
</script>

<style lang="scss" scoped>
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
.override-branch-expanded td {
  background-color: var(--v-neutral200);
}
</style>
